import { QueryClient } from '@tanstack/react-query'
import moment from 'moment'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog'
import React, { useMemo } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import { Spinner } from '../../App/Components'
import { H2, Paragraph } from '../../Common/Typography'
import { useGetPersonDataQuery } from '../util/getPersonInfo'

import {
  useDeletePersonContractMutation,
  useGetPersonContractsQuery,
} from './util/usePersonContractQuery'

const itemMargin = { margin: '24px' }
const backButton = { maxWidth: '60px', margin: '8px' }

export const Contract: React.FC = () => {
  const queryClient = new QueryClient()
  const { personUuid } = useParams<{ [key: string]: string }>()
  const { t } = useTranslation()
  const history = useHistory()
  const currentPath = history.location.pathname

  const {
    data: personContracts,
    status,
    refetch,
  } = useGetPersonContractsQuery(personUuid)

  const personDataQuery = useGetPersonDataQuery(personUuid)
  const deleteContractMutation = useDeletePersonContractMutation(queryClient)

  const handleDelete = (contractUuid: string) => {
    deleteContractMutation.mutate(contractUuid)
  }

  useMemo(() => {
    if (deleteContractMutation.isSuccess) {
      refetch()
    }
  }, [deleteContractMutation.isSuccess])

  const accept = (contractUuid: string) => {
    handleDelete(contractUuid)
    toast(t('You have deleted the contract'), {
      icon: '🗑️',
    })
  }

  const reject = () => {
    toast(t('Cancelled'), {
      icon: 'ℹ️',
    })
  }

  const confirmDelete = (contractUuid: string) => {
    confirmDialog({
      message: 'Do you want to delete this record?',
      header: 'Delete Confirmation',
      icon: 'pi pi-info-circle',
      defaultFocus: 'reject',
      acceptClassName: 'p-button-danger',
      accept: () => accept(contractUuid),
      reject,
    })
  }

  if (status === 'pending' || personDataQuery.status === 'pending') {
    return <Spinner />
  }

  if (status === 'error' || personDataQuery.status === 'error') {
    return <div>{t('error')}</div>
  }

  const personFullName = `${personDataQuery.data.first_name} ${
    personDataQuery.data.last_name || ''
  }`

  const PersonContracts = () => {
    if (personContracts.length === 0) {
      return (
        <Paragraph m={3}>{`${t(
          'There are currently no contracts for',
        )} ${personFullName}`}</Paragraph>
      )
    }

    return personContracts.map(contract => {
      const startDate = moment(contract.start_date)
      const endDate = contract.end_date ? moment(contract.end_date) : null
      const workingMinutes = contract.contract_week_minutes
      const workingHours = Math.floor(workingMinutes / 60)
      const workingMinutesLeft = Math.floor(workingMinutes % 60)

      const isIndefinite = endDate === null
      const title = isIndefinite ? t('Permanent') : t('Fixed-term')
      const titleHours = `${workingHours} ${t('Hours')}`
      const titleMinutes = `${workingMinutesLeft} ${t('Minutes')}`

      const startText = startDate.isBefore(moment())
        ? t('started on')
        : t('starts on')

      const endText = endDate
        ? endDate.isBefore(moment())
          ? t('and has ended on')
          : t('and ends on')
        : null

      const indefiniteText = `${t(
        'This is a permanent contract',
      )} ${startText} ${startDate.format('DD/MM/YYYY')}.`

      const fixedText = `${t(
        'This contract of a fixed period',
      )} ${startText} ${startDate.format(
        'DD/MM/YYYY',
      )} ${endText} ${endDate?.format('DD/MM/YYYY')}.`

      const subTitle = isIndefinite ? indefiniteText : fixedText

      return (
        <Card
          style={itemMargin}
          key={contract.uuid}
          title={`${title} | ${titleHours} ${titleMinutes}`}
          subTitle={subTitle}
          footer={() => (
            <>
              <Button
                label={t('Edit')}
                onClick={() =>
                  history.push(`${currentPath}/edit/${contract.uuid}`)
                }
              />
              <Button
                label={t('Delete')}
                severity="secondary"
                style={{ marginLeft: '0.5em' }}
                onClick={() => confirmDelete(contract.uuid)}
              />
            </>
          )}
          className="md:w-25rem"
        />
      )
    })
  }

  return (
    <div>
      <ConfirmDialog />
      <Button
        style={backButton}
        onClick={() => history.push('/hr/persons')}
        className="fa fa-arrow-left"
      />
      <H2 m={2}>{`${t('Manage the contracts for')} ${personFullName}`}</H2>
      <PersonContracts />
      <Button
        style={itemMargin}
        onClick={() => history.push(`${currentPath}/create`)}
        label={t('Add')}
      />
    </div>
  )
}
